"use client";

import Link from "next/link";
import {
  ArrowRight,
  ChevronRight,
  Globe,
  Loader2,
  MapPin,
  Tag,
  Ticket,
} from "lucide-react";
import { Typography } from "@/components/shared/typography/typography";
import { cn } from "@/lib/utils";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";
import type { MarketplaceSearchPanelStatus } from "./marketplaceSearchUtils";

const PLACE_TYPE_STYLES: Record<
  Exclude<MarketplaceSearchResult["type"], "activity">,
  { bg: string; icon: React.ReactNode }
> = {
  country: {
    bg: "bg-blue-500",
    icon: <Globe className="h-5 w-5 text-white" />,
  },
  city: {
    bg: "bg-amber-700",
    icon: <MapPin className="h-5 w-5 text-white" />,
  },
  category: {
    bg: "bg-emerald-600",
    icon: <Tag className="h-5 w-5 text-white" />,
  },
};

interface SearchDropdownProps {
  status: MarketplaceSearchPanelStatus;
  results: MarketplaceSearchResult[];
  relatedResults?: MarketplaceSearchResult[];
  searchQuery: string;
  className?: string;
  onResultClick?: () => void;
  onCommitSearch?: (query: string) => void;
  onSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  desktopTall?: boolean;
  variant?: "dropdown" | "sheet";
}

function isPlaceResult(result: MarketplaceSearchResult) {
  return result.type !== "activity";
}

function splitSuggestionResults(results: MarketplaceSearchResult[]) {
  const places = results.filter(isPlaceResult);
  const products = results.filter((result) => result.type === "activity");
  return { places, products };
}

function SectionLabel({
  children,
  sheet,
  bordered = false,
}: {
  children: React.ReactNode;
  sheet: boolean;
  bordered?: boolean;
}) {
  return (
    <Typography
      variant="p-sm"
      className={cn(
        "text-text-quaternary font-medium",
        sheet ? "px-5 pt-3 pb-1 lg:px-6" : "px-4 pt-3 pb-1",
        bordered && "border-t border-gray-100"
      )}>
      {children}
    </Typography>
  );
}

function PlaceSuggestionRow({
  result,
  index,
  onResultClick,
  onCommitSearch,
  onSuggestionSelect,
  sheet = false,
}: {
  result: MarketplaceSearchResult;
  index: number;
  onResultClick?: () => void;
  onCommitSearch?: (query: string) => void;
  onSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  sheet?: boolean;
}) {
  const style =
    PLACE_TYPE_STYLES[result.type as keyof typeof PLACE_TYPE_STYLES];
  const href =
    result.href ?? `/experiences?q=${encodeURIComponent(result.title)}`;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onSuggestionSelect && !result.href) {
      event.preventDefault();
      onSuggestionSelect(result);
    } else if (onCommitSearch && !result.href) {
      event.preventDefault();
      onCommitSearch(result.title);
    }
    onResultClick?.();
  };

  return (
    <li>
      <Link
        href={href}
        onClick={handleClick}
        className={cn(
          "hover:bg-gray-blue-50 flex items-center gap-3 py-3 transition-colors",
          sheet ? "px-5 lg:px-6" : "px-4",
          !sheet && index === 0 && "bg-gray-blue-50"
        )}>
        <span
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg",
            style.bg
          )}>
          {style.icon}
        </span>
        <div className="min-w-0 flex-1">
          <Typography variant="p-sm" className="font-bold text-gray-900">
            {result.title}
          </Typography>
          <Typography variant="p-sm" className="text-gray-500">
            {result.subtitle}
          </Typography>
        </div>
      </Link>
    </li>
  );
}

function ProductSuggestionRow({
  result,
  onResultClick,
  onCommitSearch,
  onSuggestionSelect,
  sheet = false,
}: {
  result: MarketplaceSearchResult;
  onResultClick?: () => void;
  onCommitSearch?: (query: string) => void;
  onSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  sheet?: boolean;
}) {
  const href =
    result.href ?? `/experiences?q=${encodeURIComponent(result.title)}`;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onSuggestionSelect && !result.href) {
      event.preventDefault();
      onSuggestionSelect(result);
    } else if (onCommitSearch && !result.href) {
      event.preventDefault();
      onCommitSearch(result.title);
    }
    onResultClick?.();
  };

  return (
    <li>
      <Link
        href={href}
        onClick={handleClick}
        className={cn(
          "group hover:bg-gray-blue-50 flex items-center gap-3 rounded-xl py-2.5 transition-colors",
          sheet ? "mx-3 px-3 lg:mx-4" : "mx-2 px-3"
        )}>
        <span className="border-brand-200 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border">
          <Ticket className="text-brand-600 h-4 w-4" strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="p-sm" className="font-bold text-gray-900">
              {result.title}
            </Typography>
          </div>
          {result.subtitle && (
            <Typography
              variant="p-sm"
              className="mt-0.5 flex items-center gap-1 text-gray-500">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{result.subtitle}</span>
            </Typography>
          )}
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </li>
  );
}

function SuggestionResultsList({
  results,
  sheet,
  onResultClick,
  onCommitSearch,
  onSuggestionSelect,
  maxHeightClass,
}: {
  results: MarketplaceSearchResult[];
  sheet: boolean;
  onResultClick?: () => void;
  onCommitSearch?: (query: string) => void;
  onSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  maxHeightClass?: string;
}) {
  const { places, products } = splitSuggestionResults(results);

  if (places.length === 0 && products.length === 0) {
    return null;
  }

  const showPlaceSection = places.length > 0;
  const showProductSection = products.length > 0;
  const showProductHeader = showPlaceSection && showProductSection;

  return (
    <div
      className={cn(
        sheet ? "" : "thin-scrollbar overflow-y-auto py-1",
        maxHeightClass
      )}>
      {showPlaceSection && (
        <>
          {showProductHeader && (
            <SectionLabel sheet={sheet}>Places</SectionLabel>
          )}
          <ul className="divide-y divide-gray-100">
            {places.map((result, index) => (
              <PlaceSuggestionRow
                key={result.id}
                result={result}
                index={index}
                onResultClick={onResultClick}
                onCommitSearch={onCommitSearch}
                onSuggestionSelect={onSuggestionSelect}
                sheet={sheet}
              />
            ))}
          </ul>
        </>
      )}

      {showProductSection && (
        <>
          {showProductHeader && (
            <SectionLabel sheet={sheet} bordered>
              Experiences
            </SectionLabel>
          )}
          <ul
            className={cn(
              showProductHeader ? "pt-1 pb-2" : "divide-y divide-gray-100"
            )}>
            {products.map((result) => (
              <ProductSuggestionRow
                key={result.id}
                result={result}
                onResultClick={onResultClick}
                onCommitSearch={onCommitSearch}
                onSuggestionSelect={onSuggestionSelect}
                sheet={sheet}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function StatusMessage({
  status,
  searchQuery,
  sheet = false,
}: {
  status: MarketplaceSearchPanelStatus;
  searchQuery: string;
  sheet?: boolean;
}) {
  const padding = sheet ? "px-5 py-4 lg:px-6" : "px-4 py-4";

  if (status === "typing") {
    return (
      <div className={cn("flex items-center gap-2 text-gray-600", padding)}>
        <Loader2 className="text-brand-600 h-4 w-4 animate-spin" />
        <Typography variant="p-sm" className="font-medium">
          Searching for &apos;{searchQuery.trim()}&apos;…
        </Typography>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className={cn("flex items-center gap-2 text-gray-600", padding)}>
        <Loader2 className="text-brand-600 h-4 w-4 animate-spin" />
        <Typography variant="p-sm" className="font-medium">
          Fetching suggestions…
        </Typography>
      </div>
    );
  }

  return null;
}

export function SearchDropdown({
  status,
  results,
  relatedResults = [],
  searchQuery,
  className,
  onResultClick,
  onCommitSearch,
  onSuggestionSelect,
  desktopTall = false,
  variant = "dropdown",
}: SearchDropdownProps) {
  const isSheet = variant === "sheet";
  const resultsListMaxHeight = desktopTall ? "max-h-[40rem]" : "max-h-80";
  const relatedListMaxHeight = desktopTall ? "max-h-[30rem]" : "max-h-60";
  const showResults = results.length > 0;
  const showRelated = relatedResults.length > 0;
  const showStatusOnly = status === "typing" || status === "loading";

  if (!showResults && !showRelated && !showStatusOnly) return null;

  const browseLabel =
    status === "browse" && showResults ? (
      <Typography
        variant="p-sm"
        className={cn(
          "font-medium text-gray-500",
          isSheet ? "px-5 pb-1 lg:px-6" : "px-4 pt-3 pb-1 font-semibold"
        )}>
        Popular searches
      </Typography>
    ) : null;

  const skeletonList =
    status === "typing" || status === "loading" ? (
      <ul
        className={cn("divide-y divide-gray-100", isSheet ? "" : "py-1")}
        aria-hidden>
        {[0, 1, 2].map((i) => (
          <li
            key={i}
            className={cn(
              "flex items-center gap-3 py-3",
              isSheet ? "px-5 lg:px-6" : "px-4"
            )}>
            <span className="h-11 w-11 shrink-0 animate-pulse rounded-lg bg-gray-200" />
            <div className="min-w-0 flex-1 space-y-2">
              <span className="block h-4 w-3/5 animate-pulse rounded bg-gray-200" />
              <span className="block h-3 w-4/5 animate-pulse rounded bg-gray-100" />
            </div>
          </li>
        ))}
      </ul>
    ) : null;

  const resultsList =
    showResults && status !== "typing" && status !== "loading" ? (
      <SuggestionResultsList
        results={results}
        sheet={isSheet}
        onResultClick={onResultClick}
        onCommitSearch={onCommitSearch}
        onSuggestionSelect={onSuggestionSelect}
        maxHeightClass={!isSheet ? resultsListMaxHeight : undefined}
      />
    ) : null;

  const relatedBlock = showRelated ? (
    <>
      <SectionLabel sheet={isSheet} bordered>
        Related suggestions
      </SectionLabel>
      <SuggestionResultsList
        results={relatedResults}
        sheet={isSheet}
        onResultClick={onResultClick}
        onCommitSearch={onCommitSearch}
        onSuggestionSelect={onSuggestionSelect}
        maxHeightClass={!isSheet ? relatedListMaxHeight : undefined}
      />
    </>
  ) : null;

  const seeAllFooter =
    searchQuery.trim() &&
    status !== "typing" &&
    status !== "loading" &&
    (showResults || showRelated) ? (
      <div
        className={cn(
          "border-t border-gray-100 py-3",
          isSheet ? "px-5 lg:px-6" : "px-4"
        )}>
        {onCommitSearch ? (
          <button
            type="button"
            onClick={() => {
              onCommitSearch(searchQuery.trim());
              onResultClick?.();
            }}
            className="text-brand-700 hover:text-brand-800 flex items-center gap-1 text-sm font-semibold">
            See all results for &apos;{searchQuery.trim()}&apos;
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <Link
            href={`/experiences?q=${encodeURIComponent(searchQuery)}`}
            onClick={onResultClick}
            className="text-brand-700 hover:text-brand-800 flex items-center gap-1 text-sm font-semibold">
            See all results for &apos;{searchQuery.trim()}&apos;
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    ) : null;

  const panelBody = (
    <>
      {browseLabel}
      <StatusMessage
        status={status}
        searchQuery={searchQuery}
        sheet={isSheet}
      />
      {skeletonList}
      {resultsList}
      {relatedBlock}
      {seeAllFooter}
    </>
  );

  if (isSheet) {
    return (
      <div
        className={cn("rounded-lg bg-white", className)}
        onMouseDown={(e) => e.preventDefault()}
        role="listbox"
        aria-label="Search suggestions">
        <div className="thin-scrollbar flex max-h-[calc(100vh-194px)] flex-col overflow-y-auto">
          {panelBody}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-3xl border border-gray-200 bg-white",
        className
      )}
      onMouseDown={(e) => e.preventDefault()}
      role="listbox"
      aria-label="Search suggestions">
      {panelBody}
    </div>
  );
}
